/**
 * Public "Manage my order" page.
 *
 * Customer arrives via the receipt email link (`/manage/:orderId?token=...`).
 * Shows: paid upsells (with refund-via-admin notice), available upsells they
 * can still add, and an inline Stripe Embedded Checkout when they pick one.
 *
 * Same backend endpoint (`POST /orders/{id}/upsells`) used here as in the
 * post-checkout funnel screen — single source of truth.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Loader2, Truck, Wrench, Ruler, Store, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import api from "../lib/api";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

const UPSELL_ICON = {
  pro_measurement: <Ruler className="w-5 h-5" />,
  delivery: <Truck className="w-5 h-5" />,
  delivery_install: <Wrench className="w-5 h-5" />,
};

export default function ManageOrderPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const upsellSessionId = params.get("upsell_session_id") || null;

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [checkout, setCheckout] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get(`/orders/${orderId}/manage`, { params: { token } });
      setInfo(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setError("That link expired or is invalid.");
      else setError("We couldn't load your order. Try again or reply to your receipt email.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId || !token) {
      setError("Missing order or token.");
      setLoading(false);
      return;
    }
    reload();
  }, [orderId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // If Stripe just bounced back with `?upsell_session_id=`, poll until paid.
  useEffect(() => {
    if (!upsellSessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/orders/${orderId}/upsell-status`,
          { params: { session_id: upsellSessionId } });
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          toast.success("Add-on payment confirmed!");
          // Strip the query param so we don't re-poll on refresh.
          window.history.replaceState({}, "", `/manage/${orderId}?token=${token}`);
          reload();
        }
      } catch { /* swallow */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [upsellSessionId, orderId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key) => setSelected((p) => ({ ...p, [key]: !p[key] }));

  const submitAddOns = async () => {
    if (!info) return;
    const next = { ...info.upsells, ...selected };
    // Mutual exclusion: install supersedes delivery.
    if (next.delivery_install) { next.delivery = false; next.pickup = false; }
    if (next.delivery) { next.pickup = false; }
    setSaving(true);
    try {
      const r = await api.post(`/orders/${orderId}/upsells`, {
        upsells: next, origin_url: window.location.origin,
      }, { params: { token } });
      if (r.data.requires_payment) {
        setCheckout({
          client_secret: r.data.client_secret,
          session_id: r.data.session_id,
          amount: r.data.amount,
          items: r.data.items,
        });
      } else {
        toast.success("Updated.");
        setSelected({});
        reload();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Center><Loader2 className="w-6 h-6 animate-spin muted-text" /></Center>;
  if (error) return <Center><div className="text-center"><AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-600" /><p className="text-sm">{error}</p></div></Center>;
  if (!info) return null;

  const paidUpsells = info.upsells || {};
  const charges = info.upsell_charges || [];
  const refundedKeys = new Set();
  for (const c of charges) {
    for (const r of (c.refunds || [])) (r.items_refunded || []).forEach((k) => refundedKeys.add(k));
  }

  return (
    <div className="min-h-screen px-4 py-8" style={{ background: "#f7faf6" }}>
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <p className="text-xs font-bold uppercase tracking-wider brand-text mb-1">Manage your ScreenzZ order</p>
          <p className="text-lg font-bold text-[var(--brand-text)]">Order {info.order_number}</p>
          <p className="text-xs muted-text mt-1">{info.product_name} ×{info.quantity} · {money(info.pricing?.total)} total to date</p>
        </div>

        {/* What you have */}
        {(paidUpsells.pro_measurement || paidUpsells.delivery || paidUpsells.delivery_install || paidUpsells.pickup) && (
          <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-4 mb-4" data-testid="manage-current">
            <h2 className="text-sm font-bold uppercase tracking-wider muted-text mb-2">Your services</h2>
            <ul className="space-y-1.5 text-sm">
              {paidUpsells.pickup && <li className="flex items-center gap-2"><Store className="w-4 h-4 brand-text" /> Free pickup</li>}
              {paidUpsells.pro_measurement && <li className="flex items-center gap-2"><Ruler className="w-4 h-4 brand-text" /> Pro Measurement <Check className="w-3.5 h-3.5 brand-text" /></li>}
              {paidUpsells.delivery && <li className="flex items-center gap-2"><Truck className="w-4 h-4 brand-text" /> Delivery <Check className="w-3.5 h-3.5 brand-text" /></li>}
              {paidUpsells.delivery_install && <li className="flex items-center gap-2"><Wrench className="w-4 h-4 brand-text" /> Delivery + Installation <Check className="w-3.5 h-3.5 brand-text" /></li>}
            </ul>
            <p className="text-[11px] muted-text mt-3">Need to cancel a service for a refund? Reply to your receipt email and we'll take care of it.</p>
          </div>
        )}

        {/* What you can still add */}
        {info.available_upsells && info.available_upsells.length > 0 && !checkout && (
          <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-4 mb-4" data-testid="manage-available">
            <h2 className="text-sm font-bold uppercase tracking-wider muted-text mb-3">Add to your order</h2>
            <div className="space-y-2">
              {info.available_upsells.map((up) => {
                const disabled = (up.key === "delivery" || up.key === "delivery_install") && !info.in_service_area;
                return (
                  <button
                    key={up.key}
                    data-testid={`manage-upsell-${up.key}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggle(up.key)}
                    className={`w-full text-left rounded-2xl border-2 px-4 py-3 transition flex items-center gap-3 ${
                      selected[up.key]
                        ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]"
                        : "border-[var(--brand-border)] bg-white hover:border-[var(--brand-green)]/50"
                    } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    {UPSELL_ICON[up.key]}
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{up.label}</p>
                      {disabled && <p className="text-[10px] muted-text">Not available for your ZIP</p>}
                    </div>
                    <p className="font-bold text-sm">{money(up.price)}</p>
                  </button>
                );
              })}
            </div>
            <button
              data-testid="manage-confirm-btn"
              disabled={Object.values(selected).every((v) => !v) || saving}
              onClick={submitAddOns}
              className="w-full mt-3 py-3 rounded-full brand-bg text-white font-semibold disabled:opacity-40"
            >
              {saving ? <span className="inline-flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Working…</span> : "Pay & confirm"}
            </button>
          </div>
        )}

        {/* Inline Stripe checkout */}
        {checkout && stripePromise && (
          <div className="rounded-2xl overflow-hidden border-2 border-[var(--brand-green)]" data-testid="manage-embedded-checkout">
            <div className="px-4 py-3 brand-bg text-white text-sm font-semibold">
              Pay {money(checkout.amount)} for: {checkout.items.map((i) => i.label).join(", ")}
            </div>
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: checkout.client_secret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}

        {/* Charge history */}
        {charges.length > 0 && (
          <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-4 mt-4" data-testid="manage-charges">
            <h2 className="text-sm font-bold uppercase tracking-wider muted-text mb-2">Receipts</h2>
            <ul className="space-y-2 text-xs">
              {charges.map((c) => (
                <li key={c.id} className="flex items-center justify-between">
                  <span>
                    {(c.items || []).map((i) => i.label).join(", ")}
                    {c.status !== "paid" && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold uppercase">{c.status}</span>}
                  </span>
                  <span className="tabular-nums font-semibold">{money(c.amount_total)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#f7faf6" }}>{children}</div>;
}
